import React, { useState } from "react";
import { StyledDropdown } from "./style";
import { notification, Pagination, Spin, type TableColumnsType } from "antd";
import { UserActionsButton, StyledThreeDotsVertical } from "./style";
import { SocialLogo } from "./SocialLogo";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../navigation/routes";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../store/config";
import { useEffect } from "react";
import { fetchUsersStart } from "../../../../store/features/users/users-slice";
import { useDebounce } from "../../../../hooks/useDebounce";
import { PAGE_SIZE } from "../../../../common/config";
import { SortType } from "../../../../interfaces/screens/fetch-params-types";
import {
  StatusTag,
  StyledFilterButton,
  StyledFiltersBar,
  StyledSearchBar,
  StyledSortIcon,
} from "../../../../components/common/styles";
import DeleteModal from "../../../../components/common/DeleteModal";
import { StyledTable } from "../../../../components/common/styles/tableStyles";
import { IFetchParams } from "../../../../interfaces/base";
import { IAccount } from "../../../../interfaces/store/user-interfaces";
import { deleteUserStart } from "../../../../store/features/user/user-slice";

export const UserTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<string>("");

  const { users, loading, error, metadata, tableState } = useAppSelector(
    (state) => state.users
  );
  const { search, sort, pagination } = tableState;
  const [inputSearch, setInputSearch] = useState<string>(search);

  const debouncedSearch = useDebounce(inputSearch, 300);

  const {
    error: userError,
    operation: userOperation,
    requestState: userRequestState,
    loading: userLoading,
  } = useAppSelector((state) => state.user);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: PAGE_SIZE, skip: 0 }
        : pagination;

    fetchUsers({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error || userError) {
      openNotification();
    }
  }, [error, userError]);

  useEffect(() => {
    if (!openDeleteModal) {
      setCurrentUserId("");
    }
  }, [openDeleteModal]);

  useEffect(() => {
    if (
      userRequestState === "finish" &&
      (userOperation === "delete" || userOperation === "update")
    ) {
      setOpenDeleteModal(false);
      fetchUsers({});
    }
  }, [userRequestState, userOperation]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const actionsMenuItems = [
    {
      key: "edit",
      label: t("Edit"),
      onClick: (record: IAccount) =>
        navigate(`${routes.appRoutes.users}/${record._id}`, {
          state: { isEdit: true },
        }),
    },
    {
      key: "delete",
      label: t("Delete"),
      onClick: (record: IAccount) => {
        setCurrentUserId(record._id || "");
        setOpenDeleteModal(true);
      },
    },
  ];

  const columns: TableColumnsType<any> = [
    {
      title: t("Full Name"),
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: t("Email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("Registered Via"),
      dataIndex: "registeredVia",
      key: "registeredVia",
      render: (_: any, record: any) => {
        const social = record.google
          ? "google"
          : record.apple
          ? "apple"
          : record.facebook
          ? "facebook"
          : "email";

        return <SocialLogo social={social} />;
      },
    },
    {
      title: t("Registered At"),
      dataIndex: "registeredAt",
      key: "registeredAt",
      render: (_: any, record: any) => {
        return record.registeredAt
          ? new Date(record.registeredAt).toLocaleDateString()
          : "-";
      },
    },
    {
      title: t("Status"),
      dataIndex: "status",
      render: (_: any, record: any) => (
        <StatusTag active={record.subscribed}>
          {t(record.subscribed ? "Active" : "Inactive")}
        </StatusTag>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_: any, record: any) => (
        <UserActionsButton
          menu={{
            items: actionsMenuItems.map((item) => ({
              key: item.key,
              label: item.label,
              onClick: () => item.onClick(record),
            })),
          }}
          trigger={["click"]}
        >
          <StyledThreeDotsVertical />
        </UserActionsButton>
      ),
    },
  ];

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: PAGE_SIZE * (page - 1),
    };

    fetchUsers({ pagination: newPagination });
  };

  const handleSetSort = (value: SortType) => {
    fetchUsers({
      sort: value,
      pagination: { page: 0, limit: PAGE_SIZE, skip: 0 },
    });
  };

  const handleDelete = () => {
    dispatch(
      deleteUserStart({
        userId: currentUserId,
      })
    );
  };

  const fetchUsers = (body: IFetchParams) => {
    dispatch(
      fetchUsersStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
      })
    );
  };

  return (
    <>
      {contextHolder}
      <UsersTableHeader
        search={inputSearch}
        setSearch={setInputSearch}
        setSort={handleSetSort}
      />
      <Spin spinning={loading || userLoading}>
        <StyledTable
          columns={columns}
          dataSource={users}
          pagination={false}
          rowKey="_id"
          locale={{ emptyText: t("No results") }}
        />
        <Pagination
          current={metadata?.page}
          pageSize={PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={userLoading}
        />
      </Spin>
    </>
  );
};

export const UsersTableHeader = (props: {
  search: string;
  setSearch: (search: string) => void;
  setSort: (sort: SortType) => void;
}) => {
  const { search, setSearch, setSort } = props;
  const { t } = useTranslation();
  const { tableState } = useAppSelector((state) => state.users);
  const currentSortBy = tableState.sort.sortBy;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSort = (e: any) => {
    setSort({
      sortBy: e.key,
      sortOrder: ["updatedAt", "subscribed"].includes(e.key) ? -1 : 1,
    });
  };

  const sortByOptions = [
    {
      key: "fullName",
      label: t("Sort by A-Z"),
    },
    {
      key: "subscribed",
      label: t("Sort by Status"),
    },
    {
      key: "updatedAt",
      label: t("Sort by Last Update"),
    },
  ];

  return (
    <>
      <StyledFiltersBar>
        <StyledSearchBar
          placeholder={t("Search") + "..."}
          value={search}
          onChange={handleSearchChange}
          allowClear
        />
        <StyledDropdown
          menu={{
            items: sortByOptions,
            onClick: handleSort,
            selectedKeys: [currentSortBy],
          }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ width: 150, direction: "rtl" }}
        >
          <StyledFilterButton type="text" icon={<StyledSortIcon />}>
            {t("Sort")}
          </StyledFilterButton>
        </StyledDropdown>
      </StyledFiltersBar>
    </>
  );
};
